import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AnimatedSection, {
  staggerContainer,
  fadeUpVariant,
} from "../components/AnimatedSection";
import LearnMoreArrowIcon from "../components/LearnMoreArrowIcon";
import "./InnerPage.scss";

const segments = [
  {
    eyebrow: "Traders",
    title: "Capital on day one, for every shipment",
    pain: "You move goods across borders but often front the cost and wait weeks — sometimes months — for payment.",
    solution:
      "OneLedger finances each shipment upfront. You submit the trade, we deploy the capital, and you get paid on delivery. No more cash-flow gaps slowing your next deal.",
    benefits: [
      "Upfront financing per shipment",
      "No long-term credit facility required",
      "Clear settlement timeline",
      "Corridor-aware risk assessment",
    ],
  },
  {
    eyebrow: "Brokers",
    title: "Reliable loads. Faster payments. Better partners.",
    pain: "You connect buyers and sellers but depend on counterparties who don't always pay on time — which damages your reputation.",
    solution:
      "We sit in the middle of the trade, ensuring all parties are settled on delivery. Your clients see you as the reliable broker who always delivers.",
    benefits: [
      "Load reliability backed by capital",
      "Faster settlement for your network",
      "Reduced counterparty risk",
      "Stronger broker-to-client relationships",
    ],
  },
  {
    eyebrow: "Buyers",
    title: "Payment only when goods arrive",
    pain: "You're asked to pay upfront for goods you haven't received, locking up capital against uncertain delivery.",
    solution:
      "OneLedger's delivery-triggered settlement means your payment only moves when the shipment is confirmed. Capital efficiency and peace of mind.",
    benefits: [
      "Payment on delivery confirmation",
      "End-to-end shipment visibility",
      "Reduced fraud exposure",
      "Predictable working capital",
    ],
  },
  {
    eyebrow: "Drivers & Logistics",
    title: "Get paid faster, reduce friction",
    pain: "Long payment cycles mean drivers and operators often cover costs out of pocket and wait weeks for reimbursement.",
    solution:
      "Faster settlement cycles mean drivers are paid promptly after delivery. Less friction, more trust, better execution.",
    benefits: [
      "Prompt post-delivery payment",
      "Reduced invoice chasing",
      "Clearer payment expectations",
      "Better corridor reliability",
    ],
  },
  {
    eyebrow: "Credit Risk Professionals",
    title: "Institutional-grade trade credit infrastructure",
    pain: "African trade credit lacks structured data, execution oversight, and consistent counterparty accountability.",
    solution:
      "OneLedger builds the credit layer beneath each shipment — with documented execution, real counterparty data, and settlement records. The infrastructure serious credit professionals need.",
    benefits: [
      "Shipment-level credit structuring",
      "Real execution & monitoring data",
      "Corridor-specific risk intelligence",
      "Structured credit exposure to African freight",
    ],
  },
];

export default function WhoWeServePage() {
  return (
    <>
      <Navbar />
      <main className="inner-page inner-page--who-we-serve">
        <section className="inner-page__hero">
          <div className="container">
            <AnimatedSection className="inner-page__hero-content">
              <p className="inner-page__label">Who We Serve</p>
              <h1 className="inner-page__title">
                Built for every role in African trade
              </h1>
              <p className="inner-page__sub">
                From the trader sourcing goods to the credit professional
                assessing risk — OneLedger creates value at every point in the
                chain.
              </p>
            </AnimatedSection>
          </div>
        </section>

        <section className="inner-page__section">
          <div className="container">
            <motion.div
              className="serve-full"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.05 }}
            >
              {segments.map(({ eyebrow, title, pain, solution, benefits }) => (
                <motion.div
                  key={eyebrow}
                  className="serve-segment"
                  variants={fadeUpVariant}
                >
                  <div className="serve-segment__text">
                    <span className="serve-segment__eyebrow">{eyebrow}</span>
                    <h2 className="serve-segment__title">{title}</h2>
                    <div className="serve-segment__pain">
                      <span className="serve-segment__pain-label">
                        The Challenge
                      </span>
                      <p className="serve-segment__pain-text">{pain}</p>
                    </div>
                    <p className="serve-segment__solution">{solution}</p>
                  </div>
                  <div className="serve-segment__visual">
                    {benefits.map((b) => (
                      <div key={b} className="serve-segment__visual-item">
                        {b}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="inner-page__section inner-page__section--cta">
          <div className="container inner-page__cta">
            <AnimatedSection>
              <h2 className="inner-page__cta-title">
                Your corridor. Your trade. Our capital.
              </h2>
              <Link to="/contact" className="btn-learn-more">
                Get In Touch
                <LearnMoreArrowIcon />
              </Link>
            </AnimatedSection>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
